/**
 * Automation Metadata Extraction Utilities
 *
 * SOLID: S - Single Responsibility - Only handles automation metadata extraction
 * DRY: Shared by paste and focus context mechanisms
 */

import type { AutomationAttachment } from '../components/ai-chat-content/type';
import { finiteBoundsFromCanvasModel } from './canvas-bounds';

/**
 * Extract automation metadata from an automation block
 * Used by: Copy/Paste, Focus/Selection
 */
export function extractAutomationMetadata(
  block: any,
  source: 'paste' | 'focus',
  priority: 'highest' | 'mid' | 'lowest',
  options: { docId?: string } = {}
): AutomationAttachment {
  const model = block.model ?? block;

  return {
    docId: options.docId,
    blockId: block.id ?? model.id,
    name: getAutomationTitle(model),
    bounds: finiteBoundsFromCanvasModel(model),
    sourceTableId: getStringProp(model, 'sourceTableId'),
    sourceTableName: getStringProp(model, 'sourceTableName'),
    workflowId: getStringProp(model, 'workflowId'),
    source,
    priority,
  };
}

/**
 * Get automation title from model
 */
function getAutomationTitle(model: any): string {
  if (model.props?.title?.toString) {
    const title = model.props.title.toString();
    if (title && title.trim()) {
      return title;
    }
  }
  return 'Untitled Automation';
}

/**
 * Get a string property from model props
 */
function getStringProp(model: any, propName: string): string | null {
  const value = model.props?.[propName];
  if (typeof value === 'string' && value.trim()) {
    return value;
  }
  return null;
}

/**
 * Check if a block is an automation
 */
export function isAutomationBlock(block: any): boolean {
  const flavour = block.flavour ?? block.model?.flavour;
  return flavour === 'affine:automation';
}
